// Modal handlers for the small dialogs: Add Group, Add User, Confirm,
// Change Password, Delete User.
//
// These are the "simple" modals — they show a form, validate, POST,
// and refresh. The terminal detail modal (openTerminal / closeModal)
// is huge (~300 lines, ~1200 with the settings/commands tabs) and
// stays in the inline scripts for now. Phase 9's cleanup pass will
// migrate it once the rest of the bundle is in place.

import { escapeHtml } from './utils';
import { apiFetch, type ApiFetchConfig } from './api';
import { t } from './i18n';
import type { TimestopState } from './state';
import type { Group, User } from './types';

// =====================================================================
// Add Group
// =====================================================================

export function addGroup(): void {
    const modal = document.getElementById('add-group-modal');
    if (!modal) return;
    const form = document.getElementById('add-group-form') as HTMLFormElement | null;
    if (form) form.reset();
    const err = document.getElementById('add-group-error');
    if (err) err.textContent = '';
    modal.classList.remove('hidden');
    const name = document.getElementById('add-group-name');
    if (name) setTimeout(() => name.focus(), 0);
}

export function closeAddGroupModal(): void {
    const modal = document.getElementById('add-group-modal');
    if (modal) modal.classList.add('hidden');
}

export async function submitAddGroup(ev: Event, state: TimestopState, config: ApiFetchConfig): Promise<void> {
    ev.preventDefault();
    const err = document.getElementById('add-group-error');
    const submitBtn = document.getElementById('add-group-submit') as HTMLButtonElement | null;
    const nameEl = document.getElementById('add-group-name') as HTMLInputElement | null;
    const name = (nameEl?.value || '').trim();
    if (!name) {
        if (err) err.textContent = t('groups.name_required');
        return;
    }
    if (submitBtn) submitBtn.disabled = true;
    if (err) err.textContent = '';
    try {
        const r = await apiFetch('groups', { method: 'POST', body: { name } }, config);
        if (!r.ok) throw new Error(String((r.data as { error?: string } | null)?.error || 'create_failed'));
        closeAddGroupModal();
        // Refetch groups and re-render the groups page
        const r2 = await apiFetch('groups', { method: 'GET' }, config);
        if (r2 && r2.ok && r2.data) {
            state.groups = Array.isArray(r2.data) ? r2.data : ((r2.data as { groups?: Group[] }).groups || []);
        }
        const renderFn = (window as { renderGroups?: (s: TimestopState) => void }).renderGroups;
        if (renderFn) renderFn(state);
    } catch (e) {
        if (err) err.textContent = t('groups.add_failed', { message: (e as Error).message });
    } finally {
        if (submitBtn) submitBtn.disabled = false;
    }
}

// =====================================================================
// Add User
// =====================================================================

export function addUser(state: TimestopState): void {
    const modal = document.getElementById('add-user-modal');
    if (!modal) return;
    if (!state.groups.length) {
        alert(t('users.pick_group_first'));
        return;
    }
    const form = document.getElementById('add-user-form') as HTMLFormElement | null;
    if (form) form.reset();
    const err = document.getElementById('add-user-error');
    if (err) err.textContent = '';

    // Populate the group dropdown from state.groups
    const sel = document.getElementById('add-user-group') as HTMLSelectElement | null;
    if (sel) {
        sel.innerHTML = `<option value="">${t('users.select_group_placeholder')}</option>` +
            state.groups.map((g: Group) => `<option value="${g.id}">${escapeHtml(g.name)} (#${g.id})</option>`).join('');
    }
    const roleSel = document.getElementById('add-user-role') as HTMLSelectElement | null;
    if (roleSel) roleSel.value = 'operator';
    toggleAddUserGroupField();
    modal.classList.remove('hidden');
    const username = document.getElementById('add-user-username') as HTMLInputElement | null;
    if (username) setTimeout(() => username.focus(), 0);
}

export function closeAddUserModal(): void {
    const modal = document.getElementById('add-user-modal');
    if (modal) modal.classList.add('hidden');
}

export function toggleAddUserGroupField(): void {
    const roleSel = document.getElementById('add-user-role') as HTMLSelectElement | null;
    const wrap = document.getElementById('add-user-group-wrap');
    const sel = document.getElementById('add-user-group') as HTMLSelectElement | null;
    if (!roleSel || !wrap) return;
    const isAdmin = roleSel.value === 'admin';
    if (isAdmin) {
        wrap.classList.add('hidden');
        if (sel) sel.value = '';
    } else {
        wrap.classList.remove('hidden');
    }
}

export async function submitAddUser(ev: Event, state: TimestopState, config: ApiFetchConfig): Promise<void> {
    ev.preventDefault();
    const err = document.getElementById('add-user-error');
    const submitBtn = document.getElementById('add-user-submit') as HTMLButtonElement | null;
    const usernameEl = document.getElementById('add-user-username') as HTMLInputElement | null;
    const emailEl = document.getElementById('add-user-email') as HTMLInputElement | null;
    const roleEl = document.getElementById('add-user-role') as HTMLSelectElement | null;
    const groupEl = document.getElementById('add-user-group') as HTMLSelectElement | null;
    const passwordEl = document.getElementById('add-user-password') as HTMLInputElement | null;

    const username = (usernameEl?.value || '').trim();
    const email = (emailEl?.value || '').trim();
    const role = (roleEl?.value || 'operator').trim();
    const groupIdRaw = groupEl?.value || '';
    const password = passwordEl?.value || '';

    if (!username) { if (err) err.textContent = t('users.username_required'); return; }
    if (!password) { if (err) err.textContent = t('users.password_required'); return; }
    if (password.length < 6) { if (err) err.textContent = t('users.password_too_short'); return; }

    // Admins skip the group entirely — send oid:null. Operators must
    // pick one.
    let oid: number | null = null;
    if (role === 'admin') {
        oid = null;
    } else {
        const gid = parseInt(groupIdRaw, 10);
        if (!gid || isNaN(gid)) { if (err) err.textContent = t('users.pick_group_for_operator'); return; }
        oid = gid;
    }

    if (submitBtn) submitBtn.disabled = true;
    if (err) err.textContent = '';
    try {
        const r = await apiFetch('users', {
            method: 'POST',
            body: { username, email, role, oid, pass: password },
        }, config);
        if (!r.ok) throw new Error(String((r.data as { error?: string } | null)?.error || 'create_failed'));
        closeAddUserModal();
        // Refetch users and re-render
        const r2 = await apiFetch('users', { method: 'GET' }, config);
        if (r2 && r2.ok && r2.data) {
            state.users = Array.isArray(r2.data) ? r2.data : ((r2.data as { users?: User[] }).users || []);
        }
        const renderFn = (window as { renderUsers?: (s: TimestopState, cb?: (uid: number | string, action: string) => void) => void }).renderUsers;
        if (renderFn) {
            // The renderUsers callback handles the action buttons; pass through to the inline-script wrappers.
            renderFn(state, (uid, action) => {
                const w = window as { handleUserAction?: (uid: number | string, action: string, s: TimestopState, c: ApiFetchConfig) => void };
                if (w.handleUserAction) w.handleUserAction(uid, action, state, config);
            });
        }
    } catch (e) {
        if (err) err.textContent = t('users.add_failed', { message: (e as Error).message });
    } finally {
        if (submitBtn) submitBtn.disabled = false;
    }
}

// =====================================================================
// Change Password
// =====================================================================

export async function showChangePasswordModal(user: User, config: ApiFetchConfig): Promise<void> {
    const newPass = prompt(t('users.change_password_prompt', { username: user.username }));
    if (newPass === null) return;
    if (newPass.length < 6) {
        alert(t('users.password_too_short'));
        return;
    }
    const confirmPass = prompt(t('users.change_password_confirm'));
    if (confirmPass !== newPass) {
        alert(t('users.change_password_mismatch'));
        return;
    }
    await changeUserPassword(user, newPass, config);
}

export async function changeUserPassword(user: User, newPass: string, config: ApiFetchConfig): Promise<void> {
    try {
        const r = await apiFetch('user_password', {
            method: 'POST',
            body: { id: user.id, username: user.username, pass: newPass },
        }, config);
        if (!r.ok) throw new Error(String((r.data as { error?: string } | null)?.error || 'change_failed'));
        alert(t('users.change_password_success', { username: user.username }));
    } catch (e) {
        alert(t('users.change_password_failed', { message: (e as Error).message }));
    }
}

// =====================================================================
// Delete User
// =====================================================================

export function showDeleteUserModal(user: User, state: TimestopState, config: ApiFetchConfig): void {
    const modal = document.getElementById('confirm-modal');
    const title = document.getElementById('confirm-title');
    const msg = document.getElementById('confirm-message');
    const yesBtn = document.getElementById('confirm-yes') as HTMLButtonElement | null;
    const noBtn = document.getElementById('confirm-no') as HTMLButtonElement | null;
    if (!modal || !title || !msg || !yesBtn || !noBtn) return;

    title.textContent = t('users.delete_user_title', { username: user.username });
    msg.textContent = t('users.delete_user_message');

    const yesHandler = async () => {
        yesBtn.removeEventListener('click', yesHandler);
        noBtn.removeEventListener('click', noHandler);
        modal.classList.add('hidden');
        await deleteUser(user, state, config);
    };
    const noHandler = () => {
        yesBtn.removeEventListener('click', yesHandler);
        noBtn.removeEventListener('click', noHandler);
        modal.classList.add('hidden');
    };
    yesBtn.addEventListener('click', yesHandler);
    noBtn.addEventListener('click', noHandler);
    modal.classList.remove('hidden');
}

export async function deleteUser(user: User, state: TimestopState, config: ApiFetchConfig): Promise<void> {
    try {
        const r = await apiFetch('user_delete', {
            method: 'POST',
            body: { id: user.id, username: user.username },
        }, config);
        if (!r.ok) throw new Error(String((r.data as { error?: string } | null)?.error || 'delete_failed'));
        state.users = state.users.filter((u: User) => u.id !== user.id);
        const renderFn = (window as { renderUsers?: (s: TimestopState) => void }).renderUsers;
        if (renderFn) renderFn(state);
    } catch (e) {
        alert(t('users.delete_failed', { message: (e as Error).message }));
    }
}

// Dispatch helper: called by renderUsers' action-button callback when
// data-action="changepw" / "delete" is clicked. The inline-script
// wrappers in index.php set window.handleUserAction for this purpose.
export function handleUserAction(uid: number | string, action: string, state: TimestopState, config: ApiFetchConfig): void {
    const u = state.users.find((x: User) => x.id === uid || String(x.id) === String(uid));
    if (!u) return;
    if (action === 'changepw') {
        void showChangePasswordModal(u, config);
    } else if (action === 'delete') {
        showDeleteUserModal(u, state, config);
    }
}